/**
 * Authentication Service
 * Handles login, OTP, and authentication-related API calls
 */

import { apiClient } from "../client";
import type {
  SendOTPRequest,
  SendOTPResponse,
  VerifyOTPRequest,
  VerifyOTPResponse,
  User,
} from "../types";

export const authService = {
  /**
   * Send OTP to mobile number
   * Endpoint: Auth/director-otp
   */
  async sendOTP(data: SendOTPRequest): Promise<SendOTPResponse> {
    // Ensure config is loaded before making request
    await apiClient.loadConfig();
    const response = await apiClient.post<SendOTPResponse>("Auth/director-otp", data);
    
    // Store token if provided in response
    if (response.data.token) {
      apiClient.setToken(response.data.token);
    }
    
    // Store user/firm data
    if (response.data.success) {
      const userData: User = {
        mobileNo: response.data.mobileNo,
        firmId: response.data.firmId,
        firmCode: response.data.firmCode,
        firmName: response.data.firmName,
        firmMobileNo: response.data.firmMobileNo,
        directorName: response.data.directorName,
        email: response.data.email,
      };
      localStorage.setItem("user", JSON.stringify(userData));
    }
    
    return response.data;
  },

  /**
   * Verify OTP and login
   * Note: The API endpoint Auth/director-otp returns token on first call
   * If separate verify endpoint exists, update this accordingly
   */
  async verifyOTP(data: VerifyOTPRequest): Promise<VerifyOTPResponse> {
    // Ensure config is loaded before making request
    await apiClient.loadConfig();
    // If the same endpoint is used for verification, call it again
    // Otherwise, update endpoint to the verification endpoint
    const response = await apiClient.post<VerifyOTPResponse>("Auth/director-otp", {
      mobileNo: data.mobileNo,
      firmId: data.firmId,
      mode: data.mode || "verify",
      otp: data.otp,
    });
    
    // Store token if login successful
    if (response.data.token && response.data.success) {
      apiClient.setToken(response.data.token);
      
      // Store user/firm data
      const userData: User = {
        mobileNo: response.data.mobileNo,
        firmId: response.data.firmId,
        firmCode: response.data.firmCode,
        firmName: response.data.firmName,
        firmMobileNo: response.data.firmMobileNo,
        directorName: response.data.directorName,
        email: response.data.email,
      };
      localStorage.setItem("user", JSON.stringify(userData));
    }
    
    return response.data;
  },

  /**
   * Refresh authentication token
   */
  async refreshToken(): Promise<{ token: string; expiresIn?: number }> {
    const refreshToken = localStorage.getItem("refresh_token");
    if (!refreshToken) {
      throw new Error("No refresh token available");
    }

    const response = await apiClient.post<{ token: string; expiresIn?: number }>(
      "/auth/refresh",
      { refreshToken }
    );

    if (response.data.token) {
      apiClient.setToken(response.data.token);
    }

    return response.data;
  },

  /**
   * Logout user
   */
  async logout(): Promise<void> {
    try {
      await apiClient.post("/auth/logout", {});
    } catch (error) {
      // Continue with logout even if API call fails
      console.error("Logout API error:", error);
    } finally {
      // Clear auth-related storage
      apiClient.clearToken();
      localStorage.removeItem("refresh_token");
      localStorage.removeItem("user");

      // Also clear AI Assistant chat history and conversation state
      // Keep keys in sync with useChat hook
      try {
        localStorage.removeItem("ai-assistant-chat-messages");
        localStorage.removeItem("ai-assistant-conversation-id");
        localStorage.removeItem("pharmasoft_chat_logs");
      } catch (error) {
        console.error("Error clearing AI Assistant data on logout:", error);
      }
    }
  },

  /**
   * Get current user
   */
  getCurrentUser() {
    const userStr = localStorage.getItem("user");
    if (userStr) {
      try {
        return JSON.parse(userStr);
      } catch {
        return null;
      }
    }
    return null;
  },

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return !!apiClient.getToken();
  },
};

